import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useApi } from "./useApi";
import { useActiveGithubOrgForUser } from "./useConnectedGithubOrgs";
import * as z from "zod";
import { LogLevel } from "@/components/LogTable";
import { recentEndDateAtom } from "@/atoms/runsFilterAtoms";
import { useAtom } from "jotai";
import { recentStartDateAtom } from "@/atoms/runsFilterAtoms";
const logEntrySchema = z.object({
  installation_id: z.string(),
  record_id: z.string(),
  record_name: z.string(),
  record_type: z.string(),
  record_order: z.number(),
  job_id: z.string().nullable(),
  job_name: z.string().nullable(),
  job_display_name: z.string().nullable(),
  repo: z.string(),
  workflow: z.string(),
  branch: z.string(),
  pull_request_number: z.number().transform(val => val === 0 ? null : val).nullable(),
  run_id: z.string(),
  run_attempt: z.number(),
  vm_id: z.string(),
  sender: z.string(),
  log_timestamp: z.string().transform(val => {
    const date = new Date(val);
    // Ensure we parse as UTC by appending Z if not already present
    if (!val.endsWith("Z")) {
      return new Date(val + "Z");
    }
    return date;
  }),
  message: z.string(),
  log_line_number: z.number().transform(x => x ?? 0),
  level: z.string().optional().transform(val => val ?? "info") as z.ZodType<LogLevel>
});
export type LogEntry = z.infer<typeof logEntrySchema>;

// Define cursor type for pagination
export type LogCursor = {
  log_timestamp: string;
  log_line_number: number;
  record_id: string;
};

// Schema for the response from the API
const logSearchResponseSchema = z.object({
  logs: z.array(logEntrySchema),
  next_cursor: z.object({
    log_timestamp: z.string(),
    log_line_number: z.number().transform(x => x ?? 0),
    record_id: z.string()
  }).nullable(),
  has_more_logs: z.boolean().optional()
});
export type LogSearchResponse = z.infer<typeof logSearchResponseSchema>;

/**
 * Primary hook for fetching filtered logs data with pagination support.
 *
 * @param queryString - The query string to filter the logs by.
 * @returns The logs data and pagination state.
 *
 * @example
 * const {
 *   data: logsTableData = [],
 *   isLoading: isLoadingLogs,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage,
 * } = useLogSearch(simpleText);
 */
export function useLogSearch(queryString: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const [startDate] = useAtom(recentStartDateAtom);
  const [endDate] = useAtom(recentEndDateAtom);

  // State to track all loaded logs
  const [allLogs, setAllLogs] = useState<LogEntry[]>([]);
  // State to track if there are more logs to load
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  // State to track the next cursor for pagination
  const [nextCursor, setNextCursor] = useState<LogCursor | null>(null);
  // State to track if we're loading the next page
  const [isLoadingNextPage, setIsLoadingNextPage] = useState<boolean>(false);
  // State to track the current query version to handle out-of-order responses
  const queryVersionRef = useRef(0);
  const currentQueryVersion = useRef(0);

  // Base query key (without cursor)
  const baseQueryKey = useMemo(() => ["logSearch", activeGithubOrg || "", startDate.toISOString(), endDate.toISOString(), queryString], [activeGithubOrg, startDate, endDate, queryString]);

  // Function to fetch the next page of logs
  const fetchNextPage = useCallback(async () => {
    if (!hasNextPage || !nextCursor || !activeGithubOrg || isLoadingNextPage) return;
    const nextPageQueryKey = [...baseQueryKey, nextCursor];
    const thisQueryVersion = currentQueryVersion.current;
    try {
      // Set loading state to true
      setIsLoadingNextPage(true);

      // Check if we already have this data cached
      const existingData = queryClient.getQueryData<LogSearchResponse>(nextPageQueryKey);
      if (existingData) {
        // Only update if this is still the current query version
        if (thisQueryVersion === currentQueryVersion.current) {
          setIsLoadingNextPage(false);
          setAllLogs(prev => [...prev, ...existingData.logs]);
          setNextCursor(existingData.next_cursor);
          setHasNextPage(!!existingData.next_cursor || !!existingData.has_more_logs);
        }
        return;
      }

      // Fetch the next page
      const response = await fetchLogSearch(api, activeGithubOrg, {
        from: startDate,
        to: endDate
      }, queryString, nextCursor);

      // Only process response if this is still the current query version
      if (thisQueryVersion === currentQueryVersion.current) {
        // Process and update the logs
        const newLogs = response.logs.map(log => ({
          ...log
        }));

        // Update states
        setAllLogs(prev_0 => [...prev_0, ...newLogs]);
        setNextCursor(response.next_cursor);
        setHasNextPage(!!response.next_cursor || !!response.has_more_logs);

        // Cache the response
        queryClient.setQueryData<LogSearchResponse>(nextPageQueryKey, {
          ...response
        });
      }
      return response;
    } catch (error) {
      console.error("Error fetching next page:", error);
      if (thisQueryVersion === currentQueryVersion.current) {
        setHasNextPage(false);
      }
    } finally {
      if (thisQueryVersion === currentQueryVersion.current) {
        setIsLoadingNextPage(false);
      }
    }
  }, [activeGithubOrg, api, baseQueryKey, endDate, hasNextPage, isLoadingNextPage, nextCursor, queryClient, queryString, startDate]);

  // TODO: `allLogs` is reset inside the query fetcher (lines below), but when
  // React Query serves a cached result within the 5-minute staleTime window the
  // fetcher never runs, so `allLogs` keeps showing data from the *previous*
  // filter combination. This causes stale logs to persist when toggling filters
  // back to a previously-visited query string (e.g. select user A, add user B,
  // then deselect user A — the table still shows A+B results). Affects all
  // filter types, not just users. Fix by moving the `allLogs`/`nextCursor`/
  // `hasNextPage` reset into a `useEffect` keyed on `baseQueryKey` so it fires
  // regardless of cache hits.

  // Main query for the first page
  const result = useQuery(baseQueryKey, async () => {
    if (!activeGithubOrg || !startDate || !endDate) return [];

    // Increment query version for this new query
    queryVersionRef.current += 1;
    currentQueryVersion.current = queryVersionRef.current;
    const thisQueryVersion_0 = currentQueryVersion.current;

    // Reset state when filters change
    setAllLogs([]);
    setHasNextPage(true);
    setNextCursor(null);
    const response_0 = await fetchLogSearch(api, activeGithubOrg, {
      from: startDate,
      to: endDate
    }, queryString);

    // Only process response if this is still the current query version
    if (thisQueryVersion_0 === currentQueryVersion.current) {
      const formattedLogs = response_0.logs.map(log_0 => ({
        ...log_0
      }));

      // Update all logs and cursor states
      setAllLogs(formattedLogs);
      setNextCursor(response_0.next_cursor);
      setHasNextPage(!!response_0.next_cursor || !!response_0.has_more_logs);
      return formattedLogs;
    }

    // If this is a stale query, return empty array to avoid overwriting newer data
    return [];
  }, {
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
  return {
    ...result,
    data: allLogs.length > 0 ? allLogs : result.data || [],
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage: isLoadingNextPage
  };
}

/**
 * Fetches the next page of logs from the API.
 *
 * @param api - The API instance.
 * @param org - The organization name.
 * @param dateRange - The date range to filter the logs by.
 * @param query - The query string to filter the logs by.
 * @param cursor - The cursor to fetch the next page of logs from.
 * @returns The logs data and pagination state.
 */
export async function fetchLogSearch(api: ReturnType<typeof useApi>, org: string, dateRange: {
  from: Date;
  to: Date;
}, query: string, cursor?: LogCursor) {
  const params = new URLSearchParams();
  params.append("start_time", dateRange.from.toISOString());
  params.append("end_time", dateRange.to.toISOString());
  params.append("query", query);

  // Add cursor parameters if provided
  if (cursor) {
    params.append("cursor_timestamp", cursor.log_timestamp);
    params.append("cursor_record_id", cursor.record_id);
    params.append("cursor_log_line_number", cursor.log_line_number.toString());
  }
  const response = await api.get(`user/github/orgs/${org}/metrics/logs/search?${params.toString()}`);
  return logSearchResponseSchema.parse(response.data);
}

/**
 * Schema for the filter options for the logs.
 */
const filterOptionSchema = z.object({
  value: z.string(),
  present: z.boolean()
});

/**
 * Schema for the filter options for the logs.
 */
const filterOptionsSchema = z.object({
  branches: z.array(filterOptionSchema).optional(),
  senders: z.array(filterOptionSchema).optional(),
  repos: z.array(filterOptionSchema).optional(),
  workflows: z.array(filterOptionSchema).optional(),
  levels: z.array(filterOptionSchema).optional().transform(x => x ?? []),
  job_names: z.array(filterOptionSchema).optional(),
  step_names: z.array(filterOptionSchema).optional()
});
export type LogSearchFilterOptionsProperty = "workflows" | "job_names" | "step_names" | "branches" | "senders" | "repos";

/**
 * Fetches the filter options for the logs.
 *
 * @param query - The query string to filter the logs by.
 * @param enabled - Whether the query should be enabled.
 */
export function useLogSearchFilterOptions(property, t0) {
  const $ = _c(13);
  const enabled = t0 === undefined ? true : t0;
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const [startDate] = useAtom(recentStartDateAtom);
  const [endDate] = useAtom(recentEndDateAtom);
  let t1;
  if ($[0] !== activeGithubOrg || $[1] !== endDate || $[2] !== property || $[3] !== startDate) {
    t1 = ["logSearchFilterOptions", activeGithubOrg, startDate, endDate, property];
    $[0] = activeGithubOrg;
    $[1] = endDate;
    $[2] = property;
    $[3] = startDate;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const optionsQueryKey = t1;
  let t2;
  if ($[5] !== activeGithubOrg || $[6] !== api || $[7] !== endDate || $[8] !== property || $[9] !== startDate) {
    t2 = async () => {
      if (!activeGithubOrg) {
        return {
          levels: [],
          branches: [],
          senders: [],
          repos: [],
          workflows: [],
          job_names: [],
          step_names: []
        };
      }
      return await fetchLogSearchFilterOptions(api, activeGithubOrg, {
        from: startDate,
        to: endDate
      }, property);
    };
    $[5] = activeGithubOrg;
    $[6] = api;
    $[7] = endDate;
    $[8] = property;
    $[9] = startDate;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const t3 = !!activeGithubOrg && enabled;
  let t4;
  if ($[11] !== t3) {
    t4 = {
      enabled: t3,
      staleTime: 300000
    };
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const queryResult = useQuery(optionsQueryKey, t2, t4);
  return queryResult;
}

/**
 * Fetches the filter options for the logs.
 *
 * @param api - The API instance.
 * @param org - The organization name.
 * @param query - The query string to filter the logs by.
 * @param dateRange - The date range to filter the logs by.
 */
export async function fetchLogSearchFilterOptions(api: ReturnType<typeof useApi>, org: string, dateRange: {
  from: Date;
  to: Date;
}, property: LogSearchFilterOptionsProperty) {
  const response = await api.get(`user/github/orgs/${org}/metrics/logs/search/filter-options?start_time=${dateRange.from.toISOString()}&end_time=${dateRange.to.toISOString()}&property=${property}`);
  return filterOptionsSchema.parse(response.data);
}

// Schema for the histogram for the logs.
const histogramSchema = z.object({
  buckets: z.array(z.object({
    start: z.string(),
    end: z.string(),
    log_count: z.number(),
    error_count: z.number(),
    warn_count: z.number(),
    info_count: z.number(),
    debug_count: z.number()
  })),
  meta: z.object({
    total_count: z.number()
  })
});
export async function fetchLogSearchHistogram(api: ReturnType<typeof useApi>, org: string, query: string, dateRange: {
  from: Date;
  to: Date;
}) {
  const response = await api.get(`user/github/orgs/${org}/metrics/logs/histogram?query=${encodeURIComponent(query)}&start_time=${dateRange.from.toISOString()}&end_time=${dateRange.to.toISOString()}`);
  return histogramSchema.parse(response.data);
}
export const useLogSearchHistogram = query => {
  const $ = _c(16);
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const [startDate] = useAtom(recentStartDateAtom);
  const [endDate] = useAtom(recentEndDateAtom);
  let t0;
  if ($[0] !== startDate) {
    t0 = startDate.toISOString();
    $[0] = startDate;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== endDate) {
    t1 = endDate.toISOString();
    $[2] = endDate;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== activeGithubOrg || $[5] !== query || $[6] !== t0 || $[7] !== t1) {
    t2 = ["logSearchHistogram", activeGithubOrg, query, t0, t1];
    $[4] = activeGithubOrg;
    $[5] = query;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const queryKey = t2;
  let t3;
  if ($[9] !== activeGithubOrg || $[10] !== api || $[11] !== endDate || $[12] !== query || $[13] !== startDate) {
    t3 = async () => {
      if (!activeGithubOrg) {
        return {
          buckets: [],
          meta: {
            total_count: 0
          }
        };
      }
      const response = await fetchLogSearchHistogram(api, activeGithubOrg, query, {
        from: startDate,
        to: endDate
      });
      return response;
    };
    $[9] = activeGithubOrg;
    $[10] = api;
    $[11] = endDate;
    $[12] = query;
    $[13] = startDate;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      suspense: false,
      staleTime: 300000
    };
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return useQuery(queryKey, t3, t4);
};